import React, { useState, useEffect, useRef, useMemo } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useDispatch } from "react-redux";
import { handleApiCall, URLS, COMPANY_TYPES, API_URL } from "src/_config";
import { HOW_DID_YOU_HEAR_OPTIONS } from "src/_config/site_statics";
import { useToaster } from "src/_common/hooks/actions/common/appToasterHook";
import { useUserApi } from "src/_common/hooks/actions/user/appUserApiHook";
import { useEventApi } from "src/_common/hooks/actions/event/appEventApiHook";
import { useAppUserDetailsSelector, useAppUserTokenSelector } from "src/_common/hooks/selectors/userSelector";
import { useAppUserAction } from "src/_common/hooks/actions/user/appUserActionHook";
import { API_BASE_URL } from "src/_config/site_urls";
import { ACTIONS, SAGA_ACTIONS } from "src/_config";
import axios from "axios";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { ReactSearchAutocomplete } from "react-search-autocomplete";
import SweetAlert from "react-bootstrap-sweetalert";
// import { useTranslation } from "react-i18next";
import { useTranslation } from "src/_common/hooks/useTranslation";
import ProfileImageUpdateModal from "../view-profile/ProfileImageUpdateModal";
import AddMemberForm from "src/components/members/AddMemberForm";
import MemberEditProfile from "./MemberEditProfile";
import { useSelector } from "react-redux";

interface EditProfileFormValues {
  // Company Details
  name: string;
  founded_year: string;
  type: string;
  no_of_employees: string;
  industry: string;
  website: string;
  referred_by: string;
  location: string;
  city: string;
  about: string;

  // Recruiter Details
  recruiter_name: string;
  recruiter_mobile: string;
  recruiter_email: string;
  recruiter_designation: string;
  recruiter_country_code: string;

  // User Details
  contact_no: string;
  phone_code: string;
  email: string;
  two_factor_auth: boolean;
  password: string;
  confirmPassword: string;
}

// Create schema factory function that uses translations
const createEditProfileSchema = (t: any, userType?: string) =>
  yup.object().shape({
    // Company Details validation
    name:
      userType === "member"
        ? yup.string().notRequired()
        : yup
            .string()
            .required(t("members.profile.editProfile.validation.companyNameRequired"))
            .max(150, "Company name must not exceed 150 characters"),
    founded_year: yup.string().required(t("members.profile.editProfile.validation.foundedYearRequired")),
    type: yup.string().required(t("members.profile.editProfile.validation.companyTypeRequired")),
    no_of_employees: yup.string().required(t("members.profile.editProfile.validation.numberOfEmployeesRequired")),
    industry: yup.string().required(t("members.profile.editProfile.validation.industryRequired")),
    website: yup.string().url(t("members.profile.editProfile.validation.validUrl")),
    referred_by: yup.string().required(t("members.profile.editProfile.validation.howDidYouHearRequired")),
    location: yup.string().required(t("members.profile.editProfile.validation.companyLocationRequired")),
    city: yup.string().required(t("members.profile.editProfile.validation.cityRequired")),
    about: yup
      .string()
      .required(t("members.profile.editProfile.validation.aboutInfoRequired"))
      .test("plain-text-length", t("members.profile.editProfile.validation.aboutInfoLimit"), function (value) {
        if (!value) return false;
        const plainText = value.replace(/<[^>]*>/g, "").trim();
        return plainText.length <= 1000 && plainText.length > 0;
      }),

    // Recruiter Details validation
    recruiter_name: yup.string().required(t("members.profile.editProfile.validation.recruiterNameRequired")),
    recruiter_mobile: yup.string().required(t("members.profile.editProfile.validation.mobileNumberRequired")),
    recruiter_email: yup
      .string()
      .email(t("members.profile.editProfile.validation.validEmail"))
      .required(t("members.profile.editProfile.validation.emailRequired")),
    recruiter_designation: yup.string().required(t("members.profile.editProfile.validation.designationRequired")),

    // User Details validation
    contact_no:
      userType === "member"
        ? yup.string().notRequired()
        : yup.string().required(t("members.profile.editProfile.validation.contactNumberRequired")),
    email:
      userType === "member"
        ? yup.string().email(t("members.profile.editProfile.validation.validEmail")).notRequired()
        : yup
            .string()
            .email(t("members.profile.editProfile.validation.validEmail"))
            .required(t("members.profile.editProfile.validation.emailRequired")),

    // Password validation (optional, but if provided, confirm password is required)
    password: yup.string().notRequired(),
    confirmPassword: yup
      .string()
      .test("passwords-match", t("members.addMember.validation.passwordsNotMatch"), function (value) {
        const { password } = this.parent;
        if (password && password.length > 0) {
          if (!value) {
            return this.createError({
              message: t("members.addMember.validation.confirmPasswordRequired"),
            });
          }
          return value === password;
        }
        return true;
      }),
  });

interface EditProfileMainProps {}

const EditProfileMain: React.FC<EditProfileMainProps> = () => {
  const { t, loading: translationsLoading } = useTranslation("member");
  const userApi = useUserApi();
  const eventApi = useEventApi();
  const toast = useToaster();
  const history = useHistory();
  const location = useLocation();
  const userSelector = useAppUserDetailsSelector();
  const userType = userSelector?.user_type || "";
  const userToken = useAppUserTokenSelector();
  const userAction = useAppUserAction();
  const dispatch = useDispatch();

  // Redux selectors (same as PositionTarget)
  const { countriesNew, citiesNew } = useSelector((state: any) => ({
    countriesNew: state.global.countriesNew,
    citiesNew: state.global.citiesNew,
  }));

  // Get query parameter to determine view type
  const queryParams = new URLSearchParams(location.search);
  const viewType = queryParams.get("view") || "personal"; // 'personal' or 'company'

  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [showDesignationDropdown, setShowDesignationDropdown] = useState(false);
  const [industries, setIndustries] = useState<Array<{ id: string; name: string }>>([]);
  const [countries, setCountries] = useState<Array<{ id: string; name: string }>>([]);
  const [cities, setCities] = useState<Array<{ id: string; name: string }>>([]);
  const [designations, setDesignations] = useState<Array<{ id: string; name: string }>>([]);
  const [filteredDesignations, setFilteredDesignations] = useState<Array<{ id: string; name: string }>>([]);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [designationSearchValue, setDesignationSearchValue] = useState("");
  const [selectedCountryId, setSelectedCountryId] = useState<string>("");
  const [selectedCityId, setSelectedCityId] = useState<string>("");
  const [selectedCountryName, setSelectedCountryName] = useState<string>("");
  const [selectedCityName, setSelectedCityName] = useState<string>("");
  const [selectedIndustryId, setSelectedIndustryId] = useState<string>("");
  const [selectedIndustryName, setSelectedIndustryName] = useState<string>("");
  const [isLoadingCountries, setIsLoadingCountries] = useState<boolean>(false);
  const [isLoadingCities, setIsLoadingCities] = useState<boolean>(false);
  const [isLoadingIndustries, setIsLoadingIndustries] = useState<boolean>(false);
  const [isLoadingDesignations, setIsLoadingDesignations] = useState<boolean>(false);
  const [countrySearchValue, setCountrySearchValue] = useState<string>("");
  const [citySearchValue, setCitySearchValue] = useState<string>("");
  const [industrySearchValue, setIndustrySearchValue] = useState<string>("");
  // Keys to force remounting autocomplete inputs to close dropdowns on select
  const [countrySelectKey, setCountrySelectKey] = useState(0);
  const [citySelectKey, setCitySelectKey] = useState(0);
  const [industrySelectKey, setIndustrySelectKey] = useState(0);
  const [hasCountryInteracted, setHasCountryInteracted] = useState(false);
  const [hasCityInteracted, setHasCityInteracted] = useState(false);
  const [hasIndustryInteracted, setHasIndustryInteracted] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [uploadedFile, setUploadedFile] = useState<{ name: string; size: number } | null>(null);
  const [uploadedLogoUrl, setUploadedLogoUrl] = useState<string | null>(null);
  const [showImageUpdateModal, setShowImageUpdateModal] = useState(false);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [isUpdatingTwoFactor, setIsUpdatingTwoFactor] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);

  // State variables to track phone country codes
  const [recruiterCountryCode, setRecruiterCountryCode] = useState<string>("");
  const [contactPhoneCode, setContactPhoneCode] = useState<string>("");

  const fileInputRef = useRef<HTMLInputElement>(null);
  const designationRef = useRef<HTMLDivElement>(null);
  const designationInputRef = useRef<HTMLDivElement>(null);
  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<EditProfileFormValues>({
    resolver: yupResolver(createEditProfileSchema(t, userType)),
    defaultValues: {
      name: "",
      founded_year: "",
      type: "",
      no_of_employees: "",
      industry: "",
      website: "",
      referred_by: "",
      location: "",
      city: "",
      about: "",
      recruiter_name: "",
      recruiter_mobile: "",
      recruiter_email: "",
      recruiter_designation: "",
      recruiter_country_code: "",
      contact_no: "",
      phone_code: "",
      email: "",
      two_factor_auth: false,
      password: "",
      confirmPassword: "",
    },
  });

  // Fetch user profile data and populate form
  const userMeCall = () => {
    handleApiCall(userApi.callGetMe, null, () => {}, toast, false, true);
  };

  const loadIndustries = (searchName: string = "") => {
    setIsLoadingIndustries(true);
    const params: any = { limit: 100, offset: 0 };
    if (searchName.trim()) {
      params.name = searchName.trim();
    }
    handleApiCall(
      eventApi.callGetIndustries,
      params,
      (resp: any) => {
        // Based on common saga structure - industries should be in resp.industries
        let industriesData = null;

        if (resp?.industries && Array.isArray(resp.industries)) {
          industriesData = resp.industries;
        } else if (resp?.data && Array.isArray(resp.data)) {
          industriesData = resp.data;
        } else if (resp && Array.isArray(resp)) {
          industriesData = resp;
        }

        if (industriesData && industriesData.length > 0) {
          const formattedIndustries = industriesData.map((industry: any) => ({
            id: industry.id?.toString() || industry.industry_id?.toString() || industry.value?.toString() || "",
            name: industry.name || industry.industry_name || industry.label || industry,
          }));
          setIndustries(formattedIndustries);
        } else {
          setIndustries([]);
        }
        setIsLoadingIndustries(false);
      },
      toast,
      false,
      true
    );
  };

  // Fetch countries from API using dispatch (same as PositionTarget)
  const getCountries = (searchName: string = "") => {
    const params: any = { limit: 100 };
    if (searchName.trim()) {
      params.name = searchName.trim();
    }
    dispatch({
      type: SAGA_ACTIONS.COMMON.COUNTRIES_NEW,
      payload: params,
    });
  };

  // Fetch cities from API based on country using dispatch (same as PositionTarget)
  const getCities = (countryId: string, searchName: string = "") => {
    if (!countryId) {
      setCities([]);
      return;
    }
    const params: any = { country_id: countryId, limit: 200 };
    if (searchName.trim()) {
      params.name = searchName.trim();
    }
    dispatch({
      type: SAGA_ACTIONS.COMMON.CITIES_NEW,
      payload: params,
    });
  };

  const loadDesignations = (searchName: string = "") => {
    setIsLoadingDesignations(true);
    const params: any = { limit: 100 };
    if (searchName.trim()) {
      params.name = searchName.trim();
    }
    handleApiCall(
      eventApi.callGetDesignations,
      params,
      (resp: any) => {
        if (resp?.designations) {
          const formattedDesignations = resp.designations.map((designation: any) => ({
            id: designation.id,
            name: designation.name,
          }));
          setDesignations(formattedDesignations);
          setFilteredDesignations(formattedDesignations);
        } else if (resp && Array.isArray(resp)) {
          const formattedDesignations = resp.map((designation: any) => ({
            id: designation.id,
            name: designation.name,
          }));
          setDesignations(formattedDesignations);
          setFilteredDesignations(formattedDesignations);
        } else if (resp?.data && Array.isArray(resp.data)) {
          const formattedDesignations = resp.data.map((designation: any) => ({
            id: designation.id || designation.designation_id,
            name: designation.name || designation.designation_name,
          }));
          setDesignations(formattedDesignations);
          setFilteredDesignations(formattedDesignations);
        }
        setIsLoadingDesignations(false);
      },
      toast,
      false,
      true
    );
  };

  const handleCountryChange = (selectedCountryId: string, selectedCountryName: string) => {
    setSelectedCountryId(selectedCountryId);
    setSelectedCountryName(selectedCountryName);
    setValue("location", selectedCountryName);
    setValue("city", ""); // Reset city when country changes
    setSelectedCityId(""); // Reset selected city ID
    setSelectedCityName(""); // Reset selected city name
    setCitySearchValue(""); // Reset city search value

    // Fetch cities for selected country
    if (selectedCountryId) {
      getCities(selectedCountryId);
    } else {
      setCities([]);
    }
  };

  // Search handlers for autocomplete
  const handleCountrySearch = (searchValue: string) => {
    setCountrySearchValue(searchValue);
    if (searchValue.length > 0) {
      getCountries(searchValue);
    } else {
      getCountries(); // Load all countries when search is cleared
    }
  };

  const handleCountrySelect = (item: any, setValue: Function) => {
    setValue("location", item.name);
    setSelectedCountryId(item.id);
    setSelectedCountryName(item.name);
    setCountrySearchValue(item.name);
    setValue("city", ""); // Clear city when country changes
    setSelectedCityId(""); // Clear selected city ID
    setSelectedCityName(""); // Clear selected city name
    setCitySearchValue(""); // Clear city search value
    setCities([]); // Clear old cities from previous country
    // Reset interaction and remount to close dropdown
    setHasCountryInteracted(false);
    setCountrySelectKey((k) => k + 1);
    getCities(item.id); // Load cities for selected country
  };

  const handleCitySearch = (searchValue: string) => {
    setCitySearchValue(searchValue);
    if (selectedCountryId) {
      if (searchValue.length > 0) {
        getCities(selectedCountryId, searchValue);
      } else {
        getCities(selectedCountryId); // Load all cities for the country when search is cleared
      }
    }
  };

  const handleCitySelect = (item: any, setValue: Function) => {
    setValue("city", item.name);
    setSelectedCityId(item.id);
    setSelectedCityName(item.name);
    setCitySearchValue(item.name);
    // Reset interaction and remount to close dropdown
    setHasCityInteracted(false);
    setCitySelectKey((k) => k + 1);
  };

  const handleIndustrySearch = (searchValue: string) => {
    setIndustrySearchValue(searchValue);
    if (searchValue.length > 0) {
      loadIndustries(searchValue);
    } else {
      loadIndustries(); // Load all industries when search is cleared
    }
  };

  const handleIndustrySelect = (item: any, setValue: Function) => {
    setValue("industry", item.name);
    setSelectedIndustryId(item.id);
    setSelectedIndustryName(item.name);
    setIndustrySearchValue(item.name);
    // Reset interaction and remount to close dropdown
    setHasIndustryInteracted(false);
    setIndustrySelectKey((k) => k + 1);
  };

  const handleDesignationSearch = (searchValue: string) => {
    if (searchValue.length > 1) {
      loadDesignations(searchValue);
    } else {
      loadDesignations(); // Load all designations when search is cleared
    }
  };

  const handleDesignationInputChange = (value: string, onChange: Function) => {
    onChange(value);
    setDesignationSearchValue(value);
    setShowDesignationDropdown(false); // Close example dropdown when typing

    if (value.length > 0) {
      const filtered = designations.filter((designation) =>
        designation.name.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredDesignations(filtered);
      setShowAutocomplete(true);
    } else {
      setFilteredDesignations(designations);
      setShowAutocomplete(false);
    }
  };

  const handleDesignationSelect = (designation: string, onChange: Function) => {
    onChange(designation);
    setDesignationSearchValue(designation);
    setShowAutocomplete(false);
    setShowDesignationDropdown(false); // Also close example dropdown
  };

  const handleDesignationFocus = () => {
    if (designations.length > 0) {
      setFilteredDesignations(designations);
      setShowAutocomplete(true);
      setShowDesignationDropdown(false); // Close example dropdown when focusing input
    }
  };

  const handleDesignationBlur = () => {
    // Delay hiding to allow for option clicks
    setTimeout(() => {
      setShowAutocomplete(false);
    }, 200);
  };

  // Helper function to format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  // Format phone number for display
  const formatPhoneNumber = (phone?: string | null, phoneCode?: string) => {
    if (!phone) return "Not provided";
    if (!phoneCode) return phone;
    return `+${phoneCode} ${phone}`;
  };

  // Handle profile image click
  const handleProfileImageClick = () => {
    setShowImageUpdateModal(true);
  };

  // Handle delete account with confirmation
  const handleDeleteAccount = () => {
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = () => {
    setIsDeletingAccount(true);
    userApi.callDeleteAccount(
      (response: any) => {
        setIsDeletingAccount(false);
        setShowDeleteModal(false);
        toast.success(t("members.profile.editProfile.deleteAccountSuccess"));
        // Logout and redirect to login page
        setTimeout(() => {
          // Clear user data and logout
          userAction.logout();
          // Redirect to login page
          history.push(URLS.AUTH.LOGIN);
          // Reload to clear all state
          //window.location.href = URLS.AUTH.LOGIN;
        }, 1500);
      },
      (error: any) => {
        setIsDeletingAccount(false);
        toast.error(error?.message || t("members.profile.editProfile.deleteAccountError"));
      }
    );
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
  };

  // Handle two-factor authentication toggle
  const handleToggleTwoFactor = (newStatus: boolean) => {
    setIsUpdatingTwoFactor(true);

    const requestData = {
      two_fa_auth_enabled: newStatus,
    };

    handleApiCall(
      userApi.callUpdateTwoFactorAuth,
      requestData,
      (response: any) => {
        setTwoFactorEnabled(newStatus);
        setValue("two_factor_auth", newStatus);
        setIsUpdatingTwoFactor(false);

        // Refresh user data to get updated two-factor auth status
        userMeCall();
      },
      toast,
      true,
      false
    );
  };

  // Handle confirm update
  const handleConfirmUpdate = () => {
    setShowImageUpdateModal(false);
    fileInputRef.current?.click();
  };

  // Handle cancel update
  const handleCancelUpdate = () => {
    setShowImageUpdateModal(false);
  };

  // Handle file selection for profile image
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        toast.error(t("members.profile.viewProfile.messages.invalidImage"));
        return;
      }

      // Validate file size (max 25MB)
      if (file.size > 25 * 1024 * 1024) {
        toast.error(t("members.profile.viewProfile.messages.imageSizeError"));
        return;
      }

      uploadProfileImage(file);
    }
  };

  // Upload profile image API call
  const uploadProfileImage = async (file: File) => {
    setIsUploading(true);

    try {
      // Check if user is authenticated
      if (!userToken) {
        toast.error(t("members.profile.viewProfile.messages.loginRequired"));
        setIsUploading(false);
        return;
      }

      const formData = new FormData();
      formData.append("profile_image", file);

      // Get auth token from Redux store
      const token = userToken;

      const response = await axios.post(`${API_BASE_URL}upload-profile-image`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data && response.data.status === 200) {
        // Show success message from API
        toast.success(response.data.message || t("members.profile.viewProfile.messages.uploadSuccess"));

        // Update profile image in Redux state immediately
        if (response.data.data?.profile_image_url) {
          // Update user data in Redux with new profile image
          const updatedUser = {
            ...userSelector,
            profile_image: response.data.data.profile_image_url,
          };

          dispatch({
            type: ACTIONS.USER.ME,
            payload: updatedUser,
          });
        }

        // Refresh user data to get updated profile image
        userMeCall();
      } else {
        toast.error(t("members.profile.viewProfile.messages.uploadError"));
      }
    } catch (error: any) {
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error(t("members.profile.viewProfile.messages.uploadError"));
      }
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const uploadLogoToServer = async (file: File) => {
    const userId = (userSelector as any)?.user?.id || userSelector?.id;
    if (!userId) {
      toast.error(t("members.profile.viewProfile.messages.uploadError"));
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append("user_id", userId.toString());
    formData.append("logo", file);

    try {
      // Simulate progress updates
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev < 90) return prev + 10;
          return prev;
        });
      }, 100);

      const response = await fetch(API_URL.USER.COMPANY_UPLOAD_LOGO, {
        method: "POST",
        body: formData,
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (response.ok) {
        const result = await response.json();
        console.log("Logo upload success:", result);

        // Set uploaded file info
        setUploadedFile({
          name: file.name,
          size: file.size,
        });

        // Set logo URL from response or use local preview
        if (result.logo_url || result.url || result.data?.logo_url) {
          setUploadedLogoUrl(result.logo_url || result.url || result.data?.logo_url);
          setLogoPreview(result.logo_url || result.url || result.data?.logo_url);
        } else {
          // Fallback to local preview
          const reader = new FileReader();
          reader.onload = () => {
            setLogoPreview(reader.result as string);
          };
          reader.readAsDataURL(file);
        }

        toast.success(t("members.profile.viewProfile.messages.uploadSuccess"));
        // Refresh user profile to get updated logo URL
        userMeCall();
      } else {
        throw new Error(`Upload failed: ${response.statusText}`);
      }
    } catch (error) {
      console.error("Logo upload error:", error);
      toast.error(t("members.profile.viewProfile.messages.uploadError"));
      setUploadProgress(0);
    } finally {
      setIsUploading(false);
    }
  };

  const handleLogoUpload = (file: File) => {
    if (file && file.type.startsWith("image/")) {
      // Check file size (25MB limit)
      const maxSize = 25 * 1024 * 1024; // 25MB in bytes
      if (file.size > maxSize) {
        toast.error(t("members.addMember.messages.fileSizeExceeds"));
        return;
      }

      uploadLogoToServer(file);
    } else {
      toast.error(t("members.profile.viewProfile.messages.invalidImage"));
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isUploading) return; // Prevent file change during upload

    if (e.target.files && e.target.files[0]) {
      handleLogoUpload(e.target.files[0]);
    }
  };

  const handleLogoUploadOld = (file: File | null) => {
    if (file) {
      // Immediately upload the logo using the dedicated API
      const formData = new FormData();
      formData.append("logo", file);

      // Get user ID from userSelector
      const userId = (userSelector as any)?.user?.id || userSelector?.id;
      if (userId) {
        formData.append("user_id", userId.toString());
      }

      handleApiCall(
        userApi.callCompanyUploadLogo,
        formData,
        (response: any) => {
          // Update preview on successful upload
          const reader = new FileReader();
          reader.onload = (e) => {
            setLogoPreview(e.target?.result as string);
          };
          reader.readAsDataURL(file);

          // Refresh user profile to get updated logo URL
          userMeCall();
        },
        toast,
        true,
        false
      );
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (isUploading) return; // Prevent drag actions during upload

    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (isUploading) return; // Prevent drop during upload

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleLogoUpload(e.dataTransfer.files[0]);
    }
  };

  const onSubmit = (values: EditProfileFormValues) => {
    // Prepare form data for API
    const formData = new FormData();
    console.log("=== FORM SUBMIT DEBUG ===");
    console.log("Form Values on Submit:", values);
    console.log("recruiter_country_code:", values.recruiter_country_code);
    console.log("phone_code:", values.phone_code);
    console.log("Selected City ID:", selectedCityId);
    console.log("Selected Country ID:", selectedCountryId);

    // Company details
    formData.append("founded_year", values.founded_year);
    formData.append("type", values.type);
    formData.append("no_of_employees", values.no_of_employees);
    formData.append("industry", values.industry);
    formData.append("website", values.website || "");
    formData.append("referred_by", values.referred_by);

    // Find the selected country ID
    const selectedCountry = countries.find((country) => country.name === values.location);
    const locationId = selectedCountry ? selectedCountry.id : selectedCountryId || "";

    // Use the stored selected city ID instead of searching
    const cityId = selectedCityId || "";

    formData.append("location", locationId);
    formData.append("city", cityId);
    formData.append("about", values.about);

    // Recruiter details - use the country code from PhoneInput component or state
    if (values.recruiter_mobile) {
      const fullPhoneNumber = values.recruiter_mobile.toString();
      const countryCode = values.recruiter_country_code || recruiterCountryCode || "";

      console.log("Recruiter - Full Phone:", fullPhoneNumber);
      console.log("Recruiter - Country Code (from form):", values.recruiter_country_code);
      console.log("Recruiter - Country Code (from state):", recruiterCountryCode);
      console.log("Recruiter - Country Code (final):", countryCode);

      // PhoneInput provides the full number with country code (e.g., "85212312312")
      // We need to extract just the mobile part by removing the country code digits
      const phoneDigitsOnly = fullPhoneNumber.replace(/\D/g, ""); // Remove non-digits
      const countryCodeDigits = countryCode.replace(/\D/g, ""); // Remove + sign

      let mobile = phoneDigitsOnly;
      if (countryCodeDigits && phoneDigitsOnly.startsWith(countryCodeDigits)) {
        mobile = phoneDigitsOnly.substring(countryCodeDigits.length);
      }

      console.log("Recruiter - Mobile (extracted):", mobile);

      formData.append("recruiter_name", values.recruiter_name);
      formData.append("recruiter_mobile", mobile);
      formData.append("recruiter_email", values.recruiter_email);
      formData.append("recruiter_designation", values.recruiter_designation);
      formData.append("recruiter_country_code", countryCode);
    }

    // User contact details - use the country code from PhoneInput component or state
    if (values.contact_no && userType != "member") {
      const fullPhoneNumber = values.contact_no.toString();
      const countryCode = values.phone_code || contactPhoneCode || "";

      console.log("Contact - Full Phone:", fullPhoneNumber);
      console.log("Contact - Country Code (from form):", values.phone_code);
      console.log("Contact - Country Code (from state):", contactPhoneCode);
      console.log("Contact - Country Code (final):", countryCode);

      // PhoneInput provides the full number with country code (e.g., "380123456789")
      // We need to extract just the mobile part by removing the country code digits
      const phoneDigitsOnly = fullPhoneNumber.replace(/\D/g, ""); // Remove non-digits
      const countryCodeDigits = countryCode.replace(/\D/g, ""); // Remove + sign

      let mobile = phoneDigitsOnly;
      if (countryCodeDigits && phoneDigitsOnly.startsWith(countryCodeDigits)) {
        mobile = phoneDigitsOnly.substring(countryCodeDigits.length);
      }

      console.log("Contact - Mobile (extracted):", mobile);

      formData.append("contact_no", mobile);
      formData.append("phone_code", countryCode);
    }
    if (userType != "member") {
      formData.append("name", values.name);
      formData.append("email", values.email);
    }

    // Only include password fields if password has a value
    if (values.password && values.password.trim() !== "") {
      formData.append("password", values.password);
      formData.append("confirm_password", values.confirmPassword || values.password);
    }

    // Note: Two-factor authentication is handled separately via handleToggleTwoFactor function
    // Note: Company logo is handled separately via handleLogoUpload function
    if (userType == "member") {
      formData.append("company_id", userSelector?.company?.user_id);
    }

    handleApiCall(
      userApi.callUpdateUserProfile,
      formData,
      (response: any) => {
        history.push(`${URLS.AUTH.VIEW_PROFILE}${viewType === "company" ? "?view=company" : ""}`);
      },
      toast,
      true,
      false
    );
  };

  const watchedAbout = watch("about");
  const aboutCharCount = watchedAbout ? watchedAbout.replace(/<[^>]*>/g, "").trim().length : 0;

  // Memoize placeholder text to ensure it updates when translation loads
  const aboutCompanyPlaceholder = useMemo(() => {
    if (translationsLoading) {
      return "Tell us about your company..."; // Fallback text while loading
    }
    const translatedText = t("members.profile.editProfile.sections.companyDetails.placeholders.aboutCompany");
    console.log("About Company Placeholder:", translatedText);
    return translatedText;
  }, [t, translationsLoading]);

  // Watch recruiter fields for real-time updates in header
  const watchedRecruiterName = watch("recruiter_name");
  const watchedRecruiterEmail = watch("recruiter_email");
  const watchedRecruiterDesignation = watch("recruiter_designation");

  // Watch phone codes for debugging
  const watchedRecruiterCountryCode = watch("recruiter_country_code");
  const watchedPhoneCode = watch("phone_code");
  const watchedRecruiterMobile = watch("recruiter_mobile");
  const watchedContactNo = watch("contact_no");

  // Debug log whenever phone codes change
  useEffect(() => {
    console.log("Country codes changed:", {
      recruiter_country_code: watchedRecruiterCountryCode,
      phone_code: watchedPhoneCode,
      recruiter_mobile: watchedRecruiterMobile,
      contact_no: watchedContactNo,
    });
  }, [watchedRecruiterCountryCode, watchedPhoneCode, watchedRecruiterMobile, watchedContactNo]);

  useEffect(() => {
    userMeCall();
  }, []);

  // Populate form with existing user data
  useEffect(() => {
    if (userSelector) {
      // User details
      setValue("email", (userSelector as any)?.user?.email || "");

      // Set two-factor authentication status
      const twoFactorStatus = (userSelector as any)?.user?.two_fa_auth_enabled || false;
      setValue("two_factor_auth", twoFactorStatus);
      setTwoFactorEnabled(twoFactorStatus);

      // Combine country code and contact number for PhoneInput
      const userCountryCode = (userSelector as any)?.user?.phone_code;
      const userContactNo = (userSelector as any)?.user?.mobile_number || "";
      if (userContactNo) {
        // Combine country code + mobile for PhoneInput display
        const fullPhoneNumber = userCountryCode.replace("+", "") + userContactNo;
        setValue("contact_no", fullPhoneNumber);
      }
      setValue("phone_code", userCountryCode);
      setContactPhoneCode(userCountryCode); // Also set the state variable

      // Company details
      if (userSelector.company) {
        setValue("name", userSelector.company.name || "");
        setValue("founded_year", userSelector.company.founded_year || "");
        setValue("type", userSelector.company.type || "");
        setValue("no_of_employees", userSelector.company.no_of_employees || "");
        setValue("industry", userSelector.company.industry || "");
        setValue("website", userSelector.company.website || "");
        setValue("referred_by", userSelector.company.referred_by || "");
        // setValue("location", userSelector.company.location || "");
        // setValue("city", userSelector.company.city || "");
        setValue("about", userSelector.company.about || "");
        setValue("recruiter_name", userSelector.company.recruiter_name || "");

        // Combine country code and recruiter mobile for PhoneInput
        const recruiterCountryCodeValue = userSelector.company?.recruiter_country_code;
        const recruiterMobile = userSelector.company.recruiter_mobile || "";
        if (recruiterMobile) {
          // Combine country code + mobile for PhoneInput display
          const fullRecruiterPhone = recruiterCountryCodeValue?.replace("+", "") + recruiterMobile;
          setValue("recruiter_mobile", fullRecruiterPhone);
        }

        setValue("recruiter_email", userSelector.company.recruiter_email || "");
        setValue("recruiter_designation", userSelector.company.recruiter_designation || "");
        setValue("recruiter_country_code", recruiterCountryCodeValue);
        setRecruiterCountryCode(recruiterCountryCodeValue); // Also set the state variable

        if (userSelector.company.logo_url) {
          setLogoPreview(userSelector.company.logo_url);
          // Only set uploadedFile info for existing logo if we don't already have uploaded file info
          // This prevents overriding the real file info after a fresh upload
          if (!uploadedFile || uploadedFile.size === 0) {
            setUploadedFile({
              name: "company-logo.jpg", // Default name for existing logos
              size: 0, // Size unknown for existing logos
            });
          }
          setUploadedLogoUrl(userSelector.company.logo_url);
        }
      }
    }
  }, [userSelector, setValue]);

  // Initialize country and city selections from user data (using API response data directly)
  useEffect(() => {
    if (userSelector?.company) {
      // Use country_details from API response if available
      if (userSelector.company.country_details) {
        const countryId =
          userSelector.company.country_details.id?.toString() || userSelector.company.location?.toString();
        const countryName = userSelector.company.country_details.name || "";

        if (countryId) {
          setSelectedCountryId(countryId);
          setSelectedCountryName(countryName);
          setCountrySearchValue(countryName);
          setValue("location", countryName);
          // Fetch cities for the selected country
          getCities(countryId);
        }
      } else if (userSelector.company.location) {
        // Fallback: if no country_details, try to load from location ID
        const countryId = userSelector.company.location.toString();
        setSelectedCountryId(countryId);
        getCities(countryId);
      }

      // Use city_details from API response if available
      if (userSelector.company.city_details) {
        const cityId = userSelector.company.city_details.id?.toString() || userSelector.company.city?.toString();
        const cityName = userSelector.company.city_details.name || "";

        if (cityId) {
          setSelectedCityId(cityId);
          setSelectedCityName(cityName);
          setCitySearchValue(cityName);
          setValue("city", cityName);
        }
      } else if (userSelector.company.city) {
        // Fallback: if no city_details, set ID only
        const cityId = userSelector.company.city.toString();
        setSelectedCityId(cityId);
      }
    }
  }, [userSelector?.company, setValue]);

  // Fallback: Map location ID to name when user data and countries are loaded (if country_details not available)
  useEffect(() => {
    if (
      userSelector?.company?.location &&
      !userSelector?.company?.country_details &&
      countries.length > 0 &&
      !selectedCountryName
    ) {
      const foundCountry = countries.find(
        (country: { id: string; name: string }) => country.id === userSelector.company.location?.toString()
      );
      if (foundCountry) {
        setSelectedCountryName(foundCountry.name);
        setCountrySearchValue(foundCountry.name);
        setValue("location", foundCountry.name);
      }
    }
  }, [
    userSelector?.company?.location,
    userSelector?.company?.country_details,
    countries,
    selectedCountryName,
    setValue,
  ]);

  // Fallback: Map city ID to name when user data and cities are loaded (if city_details not available)
  useEffect(() => {
    if (userSelector?.company?.city && !userSelector?.company?.city_details && cities.length > 0 && !selectedCityName) {
      const foundCity = cities.find(
        (city: { id: string; name: string }) => city.id === userSelector.company.city?.toString()
      );
      if (foundCity) {
        setSelectedCityName(foundCity.name);
        setCitySearchValue(foundCity.name);
        setValue("city", foundCity.name);
      }
    }
  }, [userSelector?.company?.city, userSelector?.company?.city_details, cities, selectedCityName, setValue]);

  // Initialize industry selection from user data
  useEffect(() => {
    if (userSelector?.company?.industry) {
      const industryName = userSelector.company.industry;
      setSelectedIndustryName(industryName);
      setIndustrySearchValue(industryName);
      setValue("industry", industryName);
    }
  }, [userSelector?.company?.industry, setValue]);

  // Load master data (industries, countries, etc.)
  useEffect(() => {
    loadIndustries();
    getCountries(); // Changed from loadCountries
    loadDesignations();
  }, []);

  // Sync Redux state with local state (same as PositionTarget)
  useEffect(() => {
    if (countriesNew && countriesNew.length > 0) {
      const formattedCountries = countriesNew.map((country: any) => ({
        id: country.id?.toString() || "",
        name: country.name || "",
      }));
      setCountries(formattedCountries);
    }
  }, [countriesNew]);

  useEffect(() => {
    if (citiesNew && citiesNew.length > 0) {
      const formattedCities = citiesNew.map((city: any) => ({
        id: city.id?.toString() || "",
        name: city.name || "",
      }));
      setCities(formattedCities);
    } else if (citiesNew && citiesNew.length === 0) {
      // Clear cities when Redux returns empty array (e.g., when country changes)
      setCities([]);
    }
  }, [citiesNew]);

  // Handle click outside for designation dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (designationRef.current && !designationRef.current.contains(event.target as Node)) {
        setShowDesignationDropdown(false);
        setShowAutocomplete(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <>
      <div id="content" className="view-profile-content" style={{ width: "auto" }}>
        {/* Header Section */}
        <div className="profile-header">
          <div className="header-content">
            <div className="header-left">
              <Link
                to={`${URLS.AUTH.VIEW_PROFILE}${viewType === "company" ? "?view=company" : ""}`}
                className="back-link"
              >
                <button className="back-button">{t("members.profile.editProfile.backButton")}</button>
              </Link>
              <h1 className="page-title">{t("members.profile.editProfile.title")}</h1>
            </div>
          </div>
        </div>

        <div className="profile-content">
          <div className="container-fluid px-4">
            {userType == "member" && viewType === "personal" ? (
              <MemberEditProfile viewType={viewType} />
            ) : (
              <form onSubmit={handleSubmit(onSubmit)} className="edit-profile-form">
                {/* User Details Section */}
                {/* <div className="form-section">
                  <div className="form-grid"></div>
                </div> */}
                <div className="profile-header-card mb-4">
                  <div className="d-flex align-items-center">
                    <div className="profile-avatar me-4" style={{ position: "relative", cursor: "pointer" }}>
                      <img
                        src={
                          // (userType == "member" && viewType === "personal"
                          (userType == "member"
                            ? userSelector?.member?.profile_photo_url
                            : (userSelector as any)?.company?.profile_image_url) || "/images/user-avatar.jpg"
                        }
                        alt="Profile Avatar"
                        className="profile-avatar-img"
                        onClick={handleProfileImageClick}
                        style={{
                          opacity: isUploading ? 0.7 : 1,
                          transition: "opacity 0.3s ease",
                        }}
                      />
                      {isUploading && (
                        <div
                          style={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            color: "#007bff",
                            fontSize: "20px",
                          }}
                        >
                          <i className="fa fa-spinner fa-spin"></i>
                        </div>
                      )}
                      <input
                        type="file"
                        ref={fileInputRef}
                        style={{ display: "none" }}
                        accept="image/*"
                        onChange={handleFileSelect}
                      />
                    </div>
                    <div className="profile-basic-info flex-grow-1">
                      <h2 className="profile-name mb-1">
                        {/* {watchedRecruiterName || userSelector?.company?.recruiter_name || "User Name"} */}
                        {userType == "member" ? userSelector?.member?.name : userSelector?.company?.name || "User Name"}
                      </h2>
                      <p className="profile-designation mb-3">
                        {/* {watchedRecruiterDesignation || userSelector?.company?.recruiter_designation || ""} */}
                        {userType == "member" ? userSelector?.member?.designation : ""}
                      </p>
                    </div>
                    <div className="profile-contact-info">
                      {userType !== "member" ? (
                        <button type="button" className="btn btn-preview " onClick={handleDeleteAccount}>
                          <i className="fa fa-trash-o me-2" aria-hidden="true"></i>
                          {t("members.profile.editProfile.deleteAccount")}
                        </button>
                      ) : (
                        <>
                          <div className="contact-item mb-2">
                            <span className="contact-label">
                              {t("members.profile.viewProfile.contactInfo.phoneNumber")}
                            </span>
                            <span className="contact-value ms-2">
                              {userSelector?.user?.phone_code && userSelector?.user?.mobile_number
                                ? `${userSelector?.user?.phone_code} ${userSelector?.user?.mobile_number}`
                                : t("members.profile.personalInfo.notProvided")}
                            </span>
                          </div>
                          <div className="contact-item">
                            <span className="contact-label">{t("members.profile.viewProfile.contactInfo.email")}</span>
                            <span className="contact-value ms-2">
                              {userSelector?.user?.email || t("members.profile.personalInfo.notProvided")}
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {userType != "member" && (
                  <div className="form-section">
                    <div className="form-grid">
                      <div className="form-group">
                        <label className="form-label">
                          {t("members.profile.editProfile.sections.companyDetails.fields.emailAddress")}{" "}
                          <span className="mandatory-mark">*</span>
                        </label>
                        <Controller
                          name="email"
                          control={control}
                          render={({ onChange, onBlur, value }) => (
                            <input
                              onChange={onChange}
                              onBlur={onBlur}
                              value={value || ""}
                              type="email"
                              className={`form-control ${errors.email ? "is-invalid" : ""}`}
                              placeholder={t(
                                "members.profile.editProfile.sections.companyDetails.placeholders.emailAddress"
                              )}
                            />
                          )}
                        />
                        {errors.email && <div className="invalid-feedback">{errors.email.message}</div>}
                      </div>

                      <div className="figma-form-group">
                        <label className="figma-label">
                          {t("members.profile.editProfile.sections.companyDetails.fields.phoneNumber")}{" "}
                          <span className="mandatory-mark">*</span>
                        </label>
                        <div className="figma-phone-wrapper">
                          <Controller
                            name="contact_no"
                            control={control}
                            render={({ onChange, onBlur, value }) => (
                              <PhoneInput
                                country={"us"}
                                value={value || ""}
                                onChange={(phone: string, country: any) => {
                                  console.log("Contact PhoneInput onChange:", phone, "dialCode:", country.dialCode);
                                  onChange(phone);
                                  const code = `+${country.dialCode}`;
                                  setContactPhoneCode(code);
                                  setValue("phone_code", code);
                                  console.log("Contact phone_code set to:", code);
                                }}
                                onBlur={onBlur}
                                placeholder={t(
                                  "members.profile.editProfile.sections.companyDetails.placeholders.phoneNumber"
                                )}
                                inputClass={`figma-phone-input ${errors.contact_no ? "error" : ""}`}
                              />
                            )}
                          />
                        </div>
                        {errors.contact_no && <span className="figma-error-text">{errors.contact_no.message}</span>}
                      </div>

                      <div className="form-group">
                        <label className="form-label" htmlFor="password">
                          {t("members.addMember.form.password")}
                        </label>
                        <Controller
                          control={control}
                          name="password"
                          render={({ onChange, onBlur, value, name, ref }) => (
                            <input
                              type="password"
                              id="password"
                              placeholder={t("members.addMember.placeholders.enterPassword")}
                              className={`form-control ${errors.password ? "is-invalid" : ""}`}
                              onChange={onChange}
                              onBlur={onBlur}
                              value={value || ""}
                              ref={ref}
                              name={name}
                            />
                          )}
                        />
                        <span className="info-red">
                          [Note: If you don't want to change your password, leave these fields blank]
                        </span>
                        {errors.password && <div className="invalid-feedback">{errors.password.message}</div>}
                      </div>

                      <div className="form-group">
                        <label className="form-label" htmlFor="confirmPassword">
                          {t("members.addMember.form.confirmPassword")}
                        </label>
                        <Controller
                          control={control}
                          name="confirmPassword"
                          render={({ onChange, onBlur, value, name, ref }) => (
                            <input
                              type="password"
                              id="confirmPassword"
                              placeholder={t("members.addMember.placeholders.enterConfirmPassword")}
                              className={`form-control ${errors.confirmPassword ? "is-invalid" : ""}`}
                              onChange={onChange}
                              onBlur={onBlur}
                              value={value || ""}
                              ref={ref}
                              name={name}
                            />
                          )}
                        />
                        <span className="info-red">
                          [Note: If you don't want to change your password, leave these fields blank]
                        </span>
                        {errors.confirmPassword && (
                          <div className="invalid-feedback">{errors.confirmPassword.message}</div>
                        )}
                      </div>

                      <div className="form-group">
                        <label className="form-label">
                          {t("members.profile.editProfile.sections.companyDetails.fields.twoFactorAuth")}
                        </label>
                        <Controller
                          name="two_factor_auth"
                          control={control}
                          render={({ onChange, value }) => (
                            <div className="two-factor-toggle-container">
                              <div className="toggle-switch-wrapper">
                                <div
                                  className="toggle-switch"
                                  onClick={() => {
                                    if (!isUpdatingTwoFactor) {
                                      const newStatus = !twoFactorEnabled;
                                      handleToggleTwoFactor(newStatus);
                                    }
                                  }}
                                  style={{ cursor: isUpdatingTwoFactor ? "not-allowed" : "pointer" }}
                                >
                                  <div className={`toggle-slider ${twoFactorEnabled ? "enabled" : "disabled"}`}></div>
                                </div>
                                <span className="toggle-label">
                                  {isUpdatingTwoFactor
                                    ? t("members.profile.editProfile.sections.companyDetails.twoFactor.updating")
                                    : twoFactorEnabled
                                    ? t("members.profile.editProfile.sections.companyDetails.twoFactor.enabled")
                                    : t("members.profile.editProfile.sections.companyDetails.twoFactor.disabled")}
                                </span>
                              </div>
                            </div>
                          )}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Company Details Section */}
                <div className="form-section">
                  <div className="section-header">
                    <h2 className="section-title">{t("members.profile.editProfile.sections.companyDetails.title")}</h2>
                    <p className="section-subtitle">
                      {t("members.profile.editProfile.sections.companyDetails.subtitle")}
                    </p>
                  </div>

                  <div className="form-grid">
                    <div className="form-group">
                      <label className="form-label">
                        {t("members.profile.editProfile.sections.companyDetails.fields.companyName")}{" "}
                        <span className="mandatory-mark">*</span>
                      </label>
                      <Controller
                        name="name"
                        control={control}
                        render={({ onChange, onBlur, value }) => (
                          <input
                            onChange={onChange}
                            onBlur={onBlur}
                            value={value || ""}
                            type="text"
                            className={`form-control ${errors.name ? "is-invalid" : ""}`}
                            placeholder={t(
                              "members.profile.editProfile.sections.companyDetails.placeholders.companyName"
                            )}
                            maxLength={150}
                          />
                        )}
                      />
                      {errors.name && <div className="invalid-feedback">{errors.name.message}</div>}
                      <div className="form-note" style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>
                        {t("members.profile.editProfile.sections.companyDetails.maxCharacterNote")}
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        {t("members.profile.editProfile.sections.companyDetails.fields.foundedYear")}{" "}
                        <span className="mandatory-mark">*</span>
                      </label>
                      <Controller
                        name="founded_year"
                        control={control}
                        render={({ onChange, onBlur, value }) => (
                          <select
                            onChange={onChange}
                            onBlur={onBlur}
                            value={value || ""}
                            className={`basic-select ${errors.founded_year ? "is-invalid" : ""}`}
                          >
                            <option value="">
                              {t("members.profile.editProfile.sections.companyDetails.dropdowns.selectYear")}
                            </option>
                            {Array.from({ length: new Date().getFullYear() - 1899 }, (_, i) => {
                              const year = new Date().getFullYear() - i;
                              return (
                                <option key={year} value={year.toString()}>
                                  {year}
                                </option>
                              );
                            })}
                          </select>
                        )}
                      />
                      {errors.founded_year && <div className="invalid-feedback">{errors.founded_year.message}</div>}
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        {t("members.profile.editProfile.sections.companyDetails.fields.companyType")}
                      </label>
                      <Controller
                        name="type"
                        control={control}
                        render={({ onChange, onBlur, value }) => (
                          <select
                            onChange={onChange}
                            onBlur={onBlur}
                            value={value || ""}
                            className={`basic-select ${errors.type ? "is-invalid" : ""}`}
                          >
                            <option value="">
                              {t("members.profile.editProfile.sections.companyDetails.dropdowns.selectType")}
                            </option>
                            {COMPANY_TYPES.map((type) => (
                              <option key={type} value={type}>
                                {type}
                              </option>
                            ))}
                          </select>
                        )}
                      />
                      {errors.type && <div className="invalid-feedback">{errors.type.message}</div>}
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        {t("members.profile.editProfile.sections.companyDetails.fields.numberOfEmployees")}{" "}
                        <span className="mandatory-mark">*</span>
                      </label>
                      <Controller
                        name="no_of_employees"
                        control={control}
                        render={({ onChange, onBlur, value }) => (
                          <input
                            onChange={onChange}
                            onBlur={onBlur}
                            value={value || ""}
                            type="number"
                            min="1"
                            className={`form-control ${errors.no_of_employees ? "is-invalid" : ""}`}
                            placeholder={t(
                              "members.profile.editProfile.sections.companyDetails.placeholders.numberOfEmployees"
                            )}
                          />
                        )}
                      />
                      {errors.no_of_employees && (
                        <div className="invalid-feedback">{errors.no_of_employees.message}</div>
                      )}
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        {t("members.profile.editProfile.sections.companyDetails.fields.industry")}{" "}
                        <span className="mandatory-mark">*</span>
                      </label>
                      <Controller
                        name="industry"
                        control={control}
                        render={({ onChange, value }) => (
                          <div className={`figma-autocomplete-wrapper ${errors.industry ? "error" : ""}`}>
                            <ReactSearchAutocomplete
                              key={industrySelectKey}
                              items={hasIndustryInteracted ? industries : []}
                              onSearch={(string, results) => {
                                setHasIndustryInteracted(true);
                                if (string.length > 0) {
                                  loadIndustries(string);
                                } else {
                                  loadIndustries();
                                }
                              }}
                              onFocus={() => {
                                if (!hasIndustryInteracted) {
                                  setHasIndustryInteracted(true);
                                  loadIndustries();
                                }
                              }}
                              onSelect={(item) => handleIndustrySelect(item, onChange)}
                              onClear={() => {
                                onChange("");
                                setSelectedIndustryId("");
                                setSelectedIndustryName("");
                                setIndustrySearchValue("");
                                setHasIndustryInteracted(false);
                                setIndustrySelectKey((k) => k + 1);
                                loadIndustries();
                              }}
                              autoFocus={false}
                              inputSearchString={selectedIndustryName || value || ""}
                              resultStringKeyName="name"
                              formatResult={(item: any) => (
                                <div className="autocomplete-result">
                                  <span>{item.name}</span>
                                </div>
                              )}
                              placeholder={t(
                                "members.profile.editProfile.sections.companyDetails.placeholders.industry"
                              )}
                              showIcon={false}
                              showNoResults={false}
                              styling={{
                                border: "1px solid #D7D7D7",
                                borderRadius: "6px",
                                backgroundColor: "#F6F6F6",
                                boxShadow: "none",
                                color: "#374151",
                                lineColor: "#e5e7eb",
                                placeholderColor: "#999",
                                zIndex: 2,
                              }}
                              fuseOptions={{ keys: ["name"] }}
                            />
                          </div>
                        )}
                      />
                      {errors.industry && <div className="invalid-feedback">{errors.industry.message}</div>}
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        {t("members.profile.editProfile.sections.companyDetails.fields.website")}
                      </label>
                      <Controller
                        name="website"
                        control={control}
                        render={({ onChange, onBlur, value }) => (
                          <input
                            onChange={onChange}
                            onBlur={onBlur}
                            value={value || ""}
                            type="url"
                            className={`form-control ${errors.website ? "is-invalid" : ""}`}
                            placeholder={t("members.profile.editProfile.sections.companyDetails.placeholders.website")}
                          />
                        )}
                      />
                      {errors.website && <div className="invalid-feedback">{errors.website.message}</div>}
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        {t("members.profile.editProfile.sections.companyDetails.fields.country")}{" "}
                        <span className="mandatory-mark">*</span>
                      </label>
                      <Controller
                        name="location"
                        control={control}
                        render={({ onChange, value }) => (
                          <div className={`figma-autocomplete-wrapper ${errors.location ? "error" : ""}`}>
                            <ReactSearchAutocomplete
                              key={countrySelectKey}
                              items={hasCountryInteracted ? countries : []}
                              onSearch={(string, results) => {
                                setHasCountryInteracted(true);
                                if (string.length > 0) {
                                  getCountries(string);
                                } else {
                                  getCountries();
                                }
                              }}
                              onFocus={() => {
                                if (!hasCountryInteracted) {
                                  setHasCountryInteracted(true);
                                  getCountries();
                                }
                              }}
                              onSelect={(item) => handleCountrySelect(item, onChange)}
                              onClear={() => {
                                onChange("");
                                setSelectedCountryId("");
                                setSelectedCountryName("");
                                setCountrySearchValue("");
                                setSelectedCityId("");
                                setSelectedCityName("");
                                setCitySearchValue("");
                                setValue("city", "");
                                setCities([]);
                                setHasCountryInteracted(false);
                                setCountrySelectKey((k) => k + 1);
                                getCountries();
                              }}
                              autoFocus={false}
                              inputSearchString={selectedCountryName || value || ""}
                              resultStringKeyName="name"
                              formatResult={(item: any) => (
                                <div className="autocomplete-result">
                                  <span>{item.name}</span>
                                </div>
                              )}
                              placeholder={t(
                                "members.profile.editProfile.sections.companyDetails.placeholders.country"
                              )}
                              showIcon={false}
                              showNoResults={false}
                              styling={{
                                border: "1px solid #D7D7D7",
                                borderRadius: "6px",
                                backgroundColor: "#F6F6F6",
                                boxShadow: "none",
                                color: "#374151",
                                lineColor: "#e5e7eb",
                                placeholderColor: "#999",
                                zIndex: 2,
                              }}
                              fuseOptions={{ keys: ["name"] }}
                            />
                          </div>
                        )}
                      />
                      {errors.location && <div className="invalid-feedback">{errors.location.message}</div>}
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        {t("members.profile.editProfile.sections.companyDetails.fields.city")}{" "}
                        <span className="mandatory-mark">*</span>
                      </label>
                      <Controller
                        name="city"
                        control={control}
                        render={({ onChange, value }) => (
                          <div className={`figma-autocomplete-wrapper ${errors.city ? "error" : ""}`}>
                            <ReactSearchAutocomplete
                              key={citySelectKey}
                              items={hasCityInteracted ? cities : []}
                              onSearch={(string, results) => {
                                setHasCityInteracted(true);
                                if (selectedCountryId) {
                                  if (string.length > 0) {
                                    getCities(selectedCountryId, string);
                                  } else {
                                    getCities(selectedCountryId);
                                  }
                                }
                              }}
                              onFocus={() => {
                                if (!hasCityInteracted && selectedCountryId) {
                                  setHasCityInteracted(true);
                                  getCities(selectedCountryId);
                                }
                              }}
                              onSelect={(item) => handleCitySelect(item, onChange)}
                              onClear={() => {
                                onChange("");
                                setSelectedCityId("");
                                setSelectedCityName("");
                                setCitySearchValue("");
                                setHasCityInteracted(false);
                                setCitySelectKey((k) => k + 1);
                                if (selectedCountryId) {
                                  getCities(selectedCountryId);
                                }
                              }}
                              autoFocus={false}
                              inputSearchString={selectedCityName || value || ""}
                              resultStringKeyName="name"
                              formatResult={(item: any) => (
                                <div className="autocomplete-result">
                                  <span>{item.name}</span>
                                </div>
                              )}
                              placeholder={
                                !selectedCountryId
                                  ? t(
                                      "members.profile.editProfile.sections.companyDetails.placeholders.selectCountryFirst"
                                    )
                                  : t("members.profile.editProfile.sections.companyDetails.placeholders.city")
                              }
                              showIcon={false}
                              showNoResults={false}
                              styling={{
                                height: "44px",
                                border: "1px solid #e5e7eb",
                                borderRadius: "4px",
                                backgroundColor: selectedCountryId ? "white" : "#f9fafb",
                                boxShadow: "none",
                                fontSize: "14px",
                                fontFamily: "inherit",
                                color: selectedCountryId ? "#374151" : "#9ca3af",
                                iconColor: "#9ca3af",
                                lineColor: "#e5e7eb",
                                placeholderColor: "#9ca3af",
                                clearIconMargin: "3px 8px 0 0",
                                zIndex: 2,
                              }}
                              fuseOptions={{ keys: ["name"] }}
                            />
                          </div>
                        )}
                      />
                      {errors.city && <div className="invalid-feedback">{errors.city.message}</div>}
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        {t("members.profile.editProfile.sections.companyDetails.fields.howDidYouHear")}{" "}
                        <span className="mandatory-mark">*</span>
                      </label>
                      <Controller
                        name="referred_by"
                        control={control}
                        render={({ onChange, onBlur, value }) => (
                          <select
                            onChange={onChange}
                            onBlur={onBlur}
                            value={value || ""}
                            className={`basic-select ${errors.referred_by ? "is-invalid" : ""}`}
                          >
                            <option value="">
                              {t("members.profile.editProfile.sections.companyDetails.dropdowns.selectOption")}
                            </option>
                            {HOW_DID_YOU_HEAR_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        )}
                      />
                      {errors.referred_by && <div className="invalid-feedback">{errors.referred_by.message}</div>}
                    </div>
                  </div>

                  <div className="form-group full-width">
                    <label className="form-label">
                      {t("members.profile.editProfile.sections.companyDetails.fields.aboutCompany")}{" "}
                      <span className="mandatory-mark">*</span>
                    </label>
                    <Controller
                      name="about"
                      control={control}
                      render={({ onChange, onBlur, value }) => (
                        <ReactQuill
                          onChange={onChange}
                          onBlur={onBlur}
                          value={value || ""}
                          className={`figma-rich-editor ${errors.about ? "error" : ""}`}
                          placeholder={aboutCompanyPlaceholder}
                          modules={{
                            toolbar: [
                              [{ header: [1, 2, 3, false] }],
                              ["bold", "italic", "underline"],
                              [{ list: "ordered" }, { list: "bullet" }],
                              ["link"],
                            ],
                          }}
                        />
                      )}
                    />
                    <div className={`figma-character-counter ${aboutCharCount > 1000 ? "over-limit" : ""}`}>
                      {aboutCharCount}/1000 {t("members.profile.editProfile.sections.companyDetails.characterCount")}
                    </div>
                    {errors.about && <div className="invalid-feedback">{errors.about.message}</div>}
                  </div>
                </div>

                {/* Recruiter Details Section */}
                <div className="form-section">
                  <div className="section-header">
                    <h2 className="section-title">
                      {t("members.profile.editProfile.sections.recruiterDetails.title")}
                    </h2>
                    <p className="section-subtitle">
                      {t("members.profile.editProfile.sections.recruiterDetails.subtitle")}
                    </p>
                  </div>

                  <div className="form-grid">
                    <div className="form-group">
                      <label className="form-label">
                        {t("members.profile.editProfile.sections.recruiterDetails.fields.name")}{" "}
                        <span className="mandatory-mark">*</span>
                      </label>
                      <Controller
                        name="recruiter_name"
                        control={control}
                        render={({ onChange, onBlur, value }) => (
                          <input
                            onChange={onChange}
                            onBlur={onBlur}
                            value={value || ""}
                            type="text"
                            className={`form-control ${errors.recruiter_name ? "is-invalid" : ""}`}
                            placeholder={t("members.profile.editProfile.sections.recruiterDetails.placeholders.name")}
                          />
                        )}
                      />
                      {errors.recruiter_name && <div className="invalid-feedback">{errors.recruiter_name.message}</div>}
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        {t("members.profile.editProfile.sections.recruiterDetails.fields.mobile")}{" "}
                        <span className="mandatory-mark">*</span>
                      </label>
                      <div className="figma-phone-wrapper">
                        <Controller
                          name="recruiter_mobile"
                          control={control}
                          render={({ onChange, onBlur, value }) => (
                            <PhoneInput
                              country={"bd"}
                              value={value || ""}
                              onChange={(phone: string, country: any) => {
                                console.log("Recruiter PhoneInput onChange:", phone, "dialCode:", country.dialCode);
                                onChange(phone);
                                const code = `+${country.dialCode}`;
                                setRecruiterCountryCode(code);
                                setValue("recruiter_country_code", code);
                                console.log("Recruiter recruiter_country_code set to:", code);
                              }}
                              onBlur={onBlur}
                              inputClass={`figma-phone-input ${errors.recruiter_mobile ? "error" : ""}`}
                              containerClass="phone-input-container"
                            />
                          )}
                        />
                      </div>
                      {errors.recruiter_mobile && (
                        <div className="invalid-feedback">{errors.recruiter_mobile.message}</div>
                      )}
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        {t("members.profile.editProfile.sections.recruiterDetails.fields.email")}{" "}
                        <span className="mandatory-mark">*</span>
                      </label>
                      <Controller
                        name="recruiter_email"
                        control={control}
                        render={({ onChange, onBlur, value }) => (
                          <input
                            onChange={onChange}
                            onBlur={onBlur}
                            value={value || ""}
                            type="email"
                            className={`form-control ${errors.recruiter_email ? "is-invalid" : ""}`}
                            placeholder={t("members.profile.editProfile.sections.recruiterDetails.placeholders.email")}
                          />
                        )}
                      />
                      {errors.recruiter_email && (
                        <div className="invalid-feedback">{errors.recruiter_email.message}</div>
                      )}
                    </div>

                    <div className="form-group designation-autocomplete">
                      <label className="form-label">
                        {t("members.profile.editProfile.sections.recruiterDetails.fields.designation")}{" "}
                        <span className="mandatory-mark">*</span>
                      </label>
                      <Controller
                        name="recruiter_designation"
                        control={control}
                        render={({ onChange, value }) => (
                          <div className="figma-autocomplete-container" ref={designationRef}>
                            <div
                              className={`figma-autocomplete-wrapper ${errors.recruiter_designation ? "error" : ""}`}
                              ref={designationInputRef}
                            >
                              <ReactSearchAutocomplete
                                items={designations}
                                onSearch={handleDesignationSearch}
                                onSelect={(item) => {
                                  onChange(item.name);
                                  setValue("recruiter_designation", item.name);
                                  // Close the dropdown by blurring the input
                                  setTimeout(() => {
                                    if (designationInputRef.current) {
                                      const input = designationInputRef.current.querySelector("input");
                                      if (input) {
                                        input.blur();
                                      }
                                    }
                                  }, 100);
                                }}
                                autoFocus={false}
                                resultStringKeyName="name"
                                inputSearchString={value || ""}
                                formatResult={(item: any) => (
                                  <div className="autocomplete-result">
                                    <span>{item.name}</span>
                                  </div>
                                )}
                                placeholder={t(
                                  "members.profile.editProfile.sections.recruiterDetails.placeholders.designation"
                                )}
                                styling={{
                                  border: "1px solid #D7D7D7",
                                  borderRadius: "6px",
                                  backgroundColor: "#F6F6F6",
                                  boxShadow: "none",
                                  color: "#374151",
                                  lineColor: "#e5e7eb",
                                  placeholderColor: "#999",
                                  zIndex: 2,
                                }}
                                fuseOptions={{ keys: ["name"] }}
                              />
                            </div>

                            {/* <button
                              type="button"
                              className="figma-example-btn"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setShowDesignationDropdown(!showDesignationDropdown);
                              }}
                            >
                              {t("members.profile.editProfile.sections.recruiterDetails.buttons.example")}
                            </button> */}
                            <span className="example-text">
                              <b>Example:</b> AI Developer
                            </span>

                            {showDesignationDropdown && (
                              <div className="designation-example-dropdown">
                                <div className="designation-options-grid">
                                  {designations.slice(0, 5).map((designation) => (
                                    <button
                                      key={designation.id}
                                      type="button"
                                      className="designation-option-btn"
                                      // onClick={(e) => {
                                      //   e.preventDefault();
                                      //   e.stopPropagation();
                                      //   onChange(designation.name);
                                      //   setValue("recruiter_designation", designation.name);
                                      //   setShowDesignationDropdown(false);
                                      //   // Also blur the input to close any autocomplete
                                      //   setTimeout(() => {
                                      //     if (designationInputRef.current) {
                                      //       const input = designationInputRef.current.querySelector("input");
                                      //       if (input) {
                                      //         input.blur();
                                      //       }
                                      //     }
                                      //   }, 100);
                                      // }}
                                    >
                                      {designation.name}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      />
                      {errors.recruiter_designation && (
                        <span className="figma-error-text">{errors.recruiter_designation.message}</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Company Logo Section */}
                <div className="form-section">
                  <div className="section-header">
                    <h2 className="section-title">{t("members.profile.editProfile.sections.companyLogo.title")}</h2>
                    <p className="section-subtitle">{t("members.profile.editProfile.sections.companyLogo.subtitle")}</p>
                  </div>

                  <div className="figma-logo-upload-container">
                    <div
                      className={`figma-upload-box ${dragActive ? "drag-active" : ""}`}
                      onDragEnter={handleDrag}
                      onDragLeave={handleDrag}
                      onDragOver={handleDrag}
                      onDrop={handleDrop}
                    >
                      {logoPreview ? (
                        <div className="figma-logo-preview">
                          <img src={logoPreview} alt="Company Logo" />
                        </div>
                      ) : (
                        <div className="figma-upload-placeholder">
                          {isUploading ? (
                            <>
                              <div className="figma-upload-text">
                                {t("members.profile.editProfile.sections.companyLogo.upload.uploading")}{" "}
                                {uploadProgress}%
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="figma-upload-icon">⬆</div>
                              <div className="figma-upload-text">
                                <span className="figma-upload-link">
                                  {t("members.profile.editProfile.sections.companyLogo.upload.clickToUpload")}
                                </span>{" "}
                                {t("members.profile.editProfile.sections.companyLogo.upload.dragAndDrop")}
                              </div>
                              <div className="figma-file-limit">
                                {t("members.profile.editProfile.sections.companyLogo.upload.fileLimit")}
                              </div>
                            </>
                          )}
                        </div>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        className="figma-file-input"
                        disabled={isUploading}
                      />
                    </div>

                    {(uploadedFile || logoPreview) && (
                      <div className="figma-file-status">
                        <div className="figma-file-item">
                          <i className="fa fa-file-text-o" aria-hidden="true"></i>

                          <div className="figma-file-details">
                            <div className="figma-filename">{uploadedFile?.name || "company-logo"}</div>
                            <div className="figma-file-size">
                              {uploadedFile?.size && uploadedFile.size > 0
                                ? formatFileSize(uploadedFile.size)
                                : t("members.profile.editProfile.sections.companyLogo.upload.existingLogo")}
                            </div>
                          </div>
                          <div
                            className="figma-delete-icon"
                            onClick={() => {
                              setUploadedFile(null);
                              setLogoPreview(null);
                              setUploadedLogoUrl(null);
                              setUploadProgress(0);
                            }}
                            style={{ cursor: "pointer" }}
                          >
                            <i className="fa fa-trash-o" aria-hidden="true"></i>
                          </div>
                        </div>
                        {(isUploading || uploadProgress > 0) && uploadedFile && (
                          <div className="figma-progress-container">
                            <div className="figma-progress-bar">
                              <div className="figma-progress-fill" style={{ width: `${uploadProgress}%` }}></div>
                            </div>
                            <span className="figma-progress-text">
                              {isUploading
                                ? `${uploadProgress}%`
                                : t("members.profile.editProfile.sections.companyLogo.upload.complete")}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Submit Button */}
                <div className="form-actions">
                  <button type="button" className="btn-cancel" onClick={() => history.push(URLS.AUTH.VIEW_PROFILE)}>
                    {t("members.profile.editProfile.buttons.cancel")}
                  </button>
                  <button type="submit" className="btn-preview">
                    {t("members.profile.editProfile.buttons.save")}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>

        {/* Custom Image Update Confirmation Modal */}
        {showImageUpdateModal && (
          <ProfileImageUpdateModal
            isVisible={showImageUpdateModal}
            onCancel={handleCancelUpdate}
            onConfirm={handleConfirmUpdate}
          />
        )}

        {/* Delete Account Confirmation Modal */}
        {showDeleteModal && (
          <SweetAlert
            warning
            showCancel
            confirmBtnText={
              isDeletingAccount ? t("members.loading.deleting") : t("members.profile.editProfile.deleteAccountYes")
            }
            confirmBtnBsStyle="danger"
            cancelBtnText={t("members.profile.editProfile.deleteAccountNo")}
            title={t("members.profile.editProfile.deleteAccount")}
            onConfirm={handleDeleteConfirm}
            onCancel={handleDeleteCancel}
            disabled={isDeletingAccount}
          >
            {t("members.profile.editProfile.deleteAccountConfirm")}
          </SweetAlert>
        )}
      </div>
    </>
  );
};

export default EditProfileMain;
